import React, { useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers-pro';

import GridPagination from '~/components/Common/Elements/GridPagination';
import PageSubheader from '~/components/Common/Elements/PageSubheader';

import { useHintClassificationChanges } from '~/hooks/apis/connection-google/useHintClassificationChanges';
import useDataGridPagination from '~/hooks/mui/useDataGridPagination';

import {
  ClassificationChange,
  ClassificationChangeTier,
  GetClassificationChangesParams,
  HintCoverageAccountType,
} from '~/services/ConnectionGoogleService';
import { Dayjs } from '~/services/TimeService';

const DEFAULT_PAGE_SIZE = 20;

/** Filters the user has submitted — pagination is merged in separately. */
type AppliedFilters = Omit<GetClassificationChangesParams, 'accountType' | 'page' | 'limit'>;

const TIER_OPTIONS: ClassificationChangeTier[] = ['HIGH', 'MEDIUM', 'LOW'];

const isTier = (value: unknown): value is ClassificationChangeTier =>
  TIER_OPTIONS.includes(value as ClassificationChangeTier);

/**
 * Rank order used only to colour the transition. Deliberately not derived from the API:
 * an unrecognised tier scores undefined and renders neutral rather than throwing.
 */
const TIER_RANK: Record<string, number> = { LOW: 0, MEDIUM: 1, HIGH: 2 };

const transitionColour = (oldTier: string, newTier: string): string => {
  const from = TIER_RANK[oldTier];
  const to = TIER_RANK[newTier];

  if (from === undefined || to === undefined || from === to) {
    return 'text.primary';
  }

  return to > from ? 'success.main' : 'error.main';
};

const TierTransition = ({
  oldTier,
  newTier,
}: {
  oldTier: ClassificationChangeTier;
  newTier: ClassificationChangeTier;
}) => (
  // height fills the grid cell so the transition sits vertically centred like every other column.
  <Stack direction="row" spacing={0.75} useFlexGap alignItems="center" height="100%">
    <Typography variant="body2" color="text.secondary">
      {oldTier}
    </Typography>
    <ArrowForwardIcon fontSize="small" color="action" />
    <Typography variant="body2" color={transitionColour(oldTier, newTier)} fontWeight="bold">
      {newTier}
    </Typography>
  </Stack>
);

/**
 * Multi-select over the tiers, following the CUG Tier filter on GHA Property Management:
 * an explicit "All" option clears the selection, and an empty selection means unfiltered.
 */
const TierMultiSelect = ({
  id,
  label,
  selected,
  onChange,
}: {
  id: string;
  label: string;
  selected: ClassificationChangeTier[];
  onChange: (tiers: ClassificationChangeTier[]) => void;
}) => (
  <FormControl size="small" fullWidth>
    <InputLabel id={`${id}-label`} shrink>
      {label}
    </InputLabel>
    <Select
      multiple
      displayEmpty
      labelId={`${id}-label`}
      label={label}
      value={selected}
      onChange={(event) => {
        const raw = event.target.value;
        const values = Array.isArray(raw) ? raw : [raw];

        if (values.includes('all')) {
          onChange([]);
          return;
        }

        onChange(values.filter(isTier));
      }}
      renderValue={(value) => (value.length === 0 ? 'All' : value.join(', '))}
    >
      <MenuItem value="all">All</MenuItem>
      {TIER_OPTIONS.map((tier) => (
        <MenuItem key={tier} value={tier}>
          {tier}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

// Every column flexes with a minWidth floor, so the row fills the grid at any viewport
// instead of leaving dead space, and the two id columns get the larger share.
const COLUMNS: Array<GridColDef<ClassificationChange>> = [
  { field: 'propertyId', headerName: 'Property ID', flex: 2, minWidth: 300 },
  {
    field: 'oldTier',
    headerName: 'Tier Change',
    flex: 1,
    minWidth: 150,
    sortable: false,
    renderCell: ({ row }) => <TierTransition oldTier={row.oldTier} newTier={row.newTier} />,
  },
  {
    field: 'createdAt',
    headerName: 'Changed At',
    flex: 1,
    minWidth: 170,
    valueGetter: (value: string | null) => (value ? new Date(value).toLocaleString() : ''),
  },
  {
    field: 'regionWeights',
    headerName: 'Region Weights',
    flex: 1,
    minWidth: 150,
    sortable: false,
    // The weights the run was computed from — "AU: 1.5, GB: 0.5", matching the Region
    // Breakdown column format on the allocated-properties grid.
    valueGetter: (value: Record<string, number> | undefined) =>
      Object.entries(value ?? {})
        .map(([region, weight]) => `${region}: ${weight}`)
        .join(', '),
  },
  { field: 'runId', headerName: 'Run ID', flex: 2, minWidth: 260 },
];

interface Props {
  accountType: HintCoverageAccountType;
}

export default function ClassificationChangeLogPanel({ accountType }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { paginationModel, setPaginationModel, setPage } = useDataGridPagination({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const [propertyIdInput, setPropertyIdInput] = useState('');
  const [oldTiersInput, setOldTiersInput] = useState<ClassificationChangeTier[]>([]);
  const [newTiersInput, setNewTiersInput] = useState<ClassificationChangeTier[]>([]);
  const [fromInput, setFromInput] = useState<Dayjs | null>(null);
  const [toInput, setToInput] = useState<Dayjs | null>(null);

  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>({});
  const [rowCount, setRowCount] = useState(0);

  const queryParams = useMemo<GetClassificationChangesParams>(
    () => ({
      accountType,
      page: paginationModel.page + 1,
      limit: paginationModel.pageSize,
      ...appliedFilters,
    }),
    [accountType, paginationModel.page, paginationModel.pageSize, appliedFilters],
  );

  const { data, isError, isFetching } = useHintClassificationChanges(queryParams);

  useEffect(() => {
    // Filters are account-scoped, so a stale property or date must not carry over.
    setPropertyIdInput('');
    setOldTiersInput([]);
    setNewTiersInput([]);
    setFromInput(null);
    setToInput(null);
    setAppliedFilters({});
    setRowCount(0);
    setPage(0);
  }, [accountType, setPage]);

  // Keep rowCount stable across fetches so the DataGrid does not reset the current page
  // to 0 while a new page query is in-flight (data is undefined).
  useEffect(() => {
    if (data?.total !== undefined) {
      setRowCount(data.total);
    }
  }, [data?.total]);

  useEffect(() => {
    if (isError) {
      enqueueSnackbar('Failed to fetch classification changes', { variant: 'error' });
    }
  }, [isError, enqueueSnackbar]);

  const dateRangeError =
    fromInput && toInput && toInput.isBefore(fromInput, 'day') ? "'Date To' must not be before 'Date From'" : '';

  const handleSearch = () => {
    setAppliedFilters({
      propertyId: propertyIdInput.trim() || undefined,
      // Empty selection means "any tier", so the param is dropped rather than sent empty.
      oldTier: oldTiersInput.length > 0 ? oldTiersInput : undefined,
      newTier: newTiersInput.length > 0 ? newTiersInput : undefined,
      createdAtStart: fromInput?.startOf('day').toISOString() || undefined,
      createdAtEnd: toInput?.endOf('day').toISOString() || undefined,
    });
    setPage(0);
  };

  const handleClearFilters = () => {
    setPropertyIdInput('');
    setOldTiersInput([]);
    setNewTiersInput([]);
    setFromInput(null);
    setToInput(null);
    setAppliedFilters({});
    setPage(0);
  };

  return (
    <Box mt={2}>
      <PageSubheader title="Classification Change Log" />

      <Alert severity="info" sx={{ mb: 2 }}>
        Tier changes recorded by classification runs — the weekly schedule and the Re-classify Properties button. Manual
        tier or forced-flag edits do not appear here, and entries are kept for 6 months.
      </Alert>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          {/* 3.5 + 1.75 + 1.75 + 1.5 + 1.5 + 2 = 12: the tier selects get the extra width since
              they render up to "HIGH, MEDIUM, LOW", dates stay compact, both buttons fit inline. */}
          <Grid size={{ xs: 12, sm: 3.5 }}>
            <TextField
              label="Property ID"
              value={propertyIdInput}
              onChange={(event) => setPropertyIdInput(event.target.value)}
              size="small"
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 1.75 }}>
            <TierMultiSelect
              id="change-log-old-tier"
              label="Tier From"
              selected={oldTiersInput}
              onChange={setOldTiersInput}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 1.75 }}>
            <TierMultiSelect
              id="change-log-new-tier"
              label="Tier To"
              selected={newTiersInput}
              onChange={setNewTiersInput}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 1.5 }}>
            <DatePicker
              label="Date From"
              value={fromInput}
              onChange={setFromInput}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 1.5 }}>
            <DatePicker
              label="Date To"
              value={toInput}
              onChange={setToInput}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  error: !!dateRangeError,
                  helperText: dateRangeError,
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            {/* height fills the cell so the buttons centre against the inputs beside them. */}
            <Stack direction="row" spacing={1} height="100%" alignItems="center">
              <Button variant="contained" onClick={handleSearch} disabled={!!dateRangeError}>
                Search
              </Button>
              <Button variant="outlined" onClick={handleClearFilters}>
                Clear
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <Box height={600}>
        <DataGrid
          rows={data?.data ?? []}
          columns={COLUMNS}
          getRowId={(row) => `${row.runId}-${row.propertyId}`}
          rowCount={rowCount}
          loading={isFetching}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50, 100]}
          slots={{ pagination: GridPagination }}
          disableRowSelectionOnClick
        />
      </Box>
    </Box>
  );
}
